import type React from 'react';
import { Analytics } from '../../analytics';
import type { DynamicQuestionItem } from '../../utils/content/dynamicQuestions';
import DashIcon from '../DashIcon';
import DynamicQuestionButton from '../DynamicQuestionButton';

interface DynamicQuestionsStripProps {
    questions: DynamicQuestionItem[];
    showDynamicQuestions: boolean;
    hasShownDynamicQuestionsOnce: boolean;
    areDynamicQuestionsCollapsed: boolean;
    onDynamicQuestionsCollapseToggle?: () => void;
    onDynamicQuestionClick?: (question: string) => void;
    dynamicQuestionAnimationKey?: number;
    effectiveLocale: string;
}

const DynamicQuestionsStrip: React.FC<DynamicQuestionsStripProps> = ({
    questions,
    showDynamicQuestions,
    hasShownDynamicQuestionsOnce,
    areDynamicQuestionsCollapsed,
    onDynamicQuestionsCollapseToggle,
    onDynamicQuestionClick,
    dynamicQuestionAnimationKey,
    effectiveLocale,
}) => {
    const shouldShowCollapseButton =
        questions.length >= 1 &&
        (showDynamicQuestions || hasShownDynamicQuestionsOnce) &&
        Boolean(onDynamicQuestionsCollapseToggle);
    const questionsToShow = areDynamicQuestionsCollapsed ? [] : questions;

    return (
        <div
            className="rose-search-bar-dynamic-questions-container relative"
            style={{
                maxHeight: showDynamicQuestions && !areDynamicQuestionsCollapsed ? '300px' : '0px',
                opacity: showDynamicQuestions && !areDynamicQuestionsCollapsed ? 1 : 0,
                overflow: 'hidden',
                transition: 'all 0.3s ease-out',
                transitionDelay:
                    showDynamicQuestions && !areDynamicQuestionsCollapsed ? '0.3s' : '0s',
            }}
        >
            <style>{`
                .collapse-dynamic-questions-button:hover {
                    background-color: var(--rw-surface-interactive-hover);
                }
            `}</style>
            {shouldShowCollapseButton && onDynamicQuestionsCollapseToggle && (
                <button
                    type="button"
                    className="collapse-dynamic-questions-button absolute top-0 right-0 p-2 rounded-full transition-colors duration-200 z-10"
                    onClick={() => {
                        Analytics.trackMinimizedDynamicQuestions({
                            isMinimized: !areDynamicQuestionsCollapsed,
                            totalQuestions: questions.length,
                            language: effectiveLocale,
                        });
                        onDynamicQuestionsCollapseToggle();
                    }}
                    aria-label={
                        areDynamicQuestionsCollapsed ? 'Show all questions' : 'Minimize questions'
                    }
                >
                    <DashIcon width={16} height={16} color="var(--rw-icon-muted)" />
                </button>
            )}

            <div
                className={`flex flex-wrap gap-[10px] items-start ${shouldShowCollapseButton ? 'pr-12' : ''}`}
            >
                {questionsToShow.slice(0, 10).map((question) => (
                    <DynamicQuestionButton
                        key={`${question.id}-${question.text}-${dynamicQuestionAnimationKey ?? ''}`}
                        question={question}
                        show={showDynamicQuestions}
                        showArrow={true}
                        onDynamicQuestionClick={onDynamicQuestionClick}
                    />
                ))}
            </div>
        </div>
    );
};

export default DynamicQuestionsStrip;
